import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import * as types from '../action-types';
import { getMoreLikeThis } from '../../decision.api';
import {
  MoreLikeThisReducerType,
  DecisionMoreLikeThisParams,
  DecisionMoreLikeThisMenuParams,
} from '../decisionState.types';
import { DecisionMoreLikeThisApi } from '../../decision.types';

export const storeMoreLikeThisMenuText = (text: string) =>
  <const>{
    type: types.storeMoreLikeThisMenuText,
    payload: text,
  };

export const showMoreLikeThisMenu = (show: boolean) =>
  <const>{
    type: types.showMoreLikeThisMenu,
    payload: show,
  };

export const openMoreLikeThisModal = (source: string) =>
  <const>{
    type: types.openMoreLikeThisModal,
    analytics: {
      type: 'Decision ModalMoreLikeThisAppeared',
      data: {
        source,
      },
    },
  };

export const closeMoreLikeThisModal = () =>
  <const>{
    type: types.closeMoreLikeThisModal,
  };

export const fetchingMoreLikeThisDecisions = () =>
  <const>{
    type: types.fetchingMoreLikeThisDecisions,
  };

export const storeMoreLikeThisDecisions = ({
  similarDecisions,
  referenceDecisions,
  source,
}: {
  similarDecisions: MoreLikeThisReducerType['similarDecisions'];
  referenceDecisions: MoreLikeThisReducerType['referenceDecisions'];
  source: string;
}) =>
  <const>{
    type: types.storeMoreLikeThisDecisions,
    payload: { similarDecisions, referenceDecisions },
    analytics: {
      type: 'Decision ModalMoreLikeThisAppearedEnd',
      data: {
        source,
        similarCount: similarDecisions.length,
        referenceCount: referenceDecisions.length,
      },
    },
  };

export const storeSelectionTooLongError = () =>
  <const>{
    type: types.storeSelectionTooLongError,
  };

export const fetchEndMoreLikeThisDecisions = () =>
  <const>{
    type: types.fetchEndMoreLikeThisDecisions,
  };

export const errorServer = () =>
  <const>{
    type: types.errorServer,
  };

const getMoreLikeThisDecisions = ({
  docId,
  docKey,
  source,
}: DecisionMoreLikeThisParams) => (
  dispatch: ThunkDispatch<{}, {}, AnyAction>,
  getState: Function,
): Promise<DecisionMoreLikeThisApi> => {
  const { selectedText } = getState().decision.decisionSelection;

  return getMoreLikeThis({ text: selectedText, docId, docKey })
    .then(({ data }) => {
      const { similarDecisions, referenceDecisions } = data;
      dispatch(
        storeMoreLikeThisDecisions({
          similarDecisions,
          referenceDecisions,
          source,
        }),
      );
      return data;
    })
    .catch(error => {
      if (!error.response || !error.response.data) {
        dispatch(errorServer());
        return Promise.reject(error);
      }

      if (error.response.data.error === 'TOO_LONG_EXTRACT') {
        dispatch(storeSelectionTooLongError());
        return Promise.reject(error);
      }

      dispatch(errorServer());

      return Promise.reject(error);
    })
    .finally(() => dispatch(fetchEndMoreLikeThisDecisions()));
};

export const fetchOpenMoreLikeThisMenu = ({
  docId,
  docKey,
  source,
  text,
}: DecisionMoreLikeThisMenuParams) => (
  dispatch: ThunkDispatch<{}, {}, AnyAction>,
): Promise<DecisionMoreLikeThisApi> => {
  dispatch(storeMoreLikeThisMenuText(text));
  dispatch(fetchingMoreLikeThisDecisions());
  dispatch(showMoreLikeThisMenu(true));
  return dispatch(
    getMoreLikeThisDecisions({
      docId,
      docKey,
      source,
    }),
  );
};

export const fetchOpenMoreLikeThisModal = ({
  docId,
  docKey,
  source,
}: DecisionMoreLikeThisParams) => (
  dispatch: ThunkDispatch<{}, {}, AnyAction>,
): Promise<DecisionMoreLikeThisApi> => {
  dispatch(showMoreLikeThisMenu(false));
  dispatch(fetchingMoreLikeThisDecisions());
  dispatch(openMoreLikeThisModal(source));
  return dispatch(
    getMoreLikeThisDecisions({
      docId,
      docKey,
      source,
    }),
  );
};

export type MoreLikeThisActions = ReturnType<
  | typeof openMoreLikeThisModal
  | typeof closeMoreLikeThisModal
  | typeof fetchingMoreLikeThisDecisions
  | typeof storeMoreLikeThisDecisions
  | typeof storeSelectionTooLongError
  | typeof fetchEndMoreLikeThisDecisions
  | typeof errorServer
  | typeof showMoreLikeThisMenu
  | typeof storeMoreLikeThisMenuText
>;
